import React, { useState, useEffect, useContext } from 'react'
import { AppContext } from 'Context/AppContext';
import TooltipOverlay from 'Components/Common/components/Button/TooltipOverlay';
import { getRecommenderModelsForGuide } from '../ModelServices/ModalServices';
import { TCategoryOption, TConnector, TField, TIndexState, TPredictiveData, TProductCategory, TRecommenderModel, TSubmodel, TUpsertISRProjectDataModelsInput } from '../model';

type TPredictiveComponentProps = {
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
};

const PredictiveComponent = ({ selectedItem, userProjectDataModels, setUserProjectDataModels, indexInfo }: TPredictiveComponentProps) => {
    const [isPredictive, setIsPredictive] = useState<boolean>(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedRecommenderId, setSelectedRecommenderId] = useState<string>("");
    const [availableCategories, setAvailableCategories] = useState<TCategoryOption[]>([]);
    const [recommenderModels, setRecommenderModels] = useState<TRecommenderModel[]>([]);
    const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false);
    const appData = useContext(AppContext);
    const channelKey = appData?.channelKey;

    // Initialize state from existing data
    useEffect(() => {
        if (selectedItem?.type === "Category") {
            const categoryItem = selectedItem as TProductCategory;

            // Set predictive toggle state
            setIsPredictive(!!categoryItem.predictive?.isPredictive);

            // Set base category if it exists
            if (categoryItem.predictive?.baseCategory && categoryItem.predictive.baseCategory.length > 0) {
                setSelectedCategoryId(categoryItem.predictive.baseCategory[0]);

                // If there's a base category and recommender ID but no models loaded, fetch them
                if (categoryItem.predictive.recommenderId && !recommenderModels.length) {
                    fetchRecommenderModels(categoryItem.predictive.baseCategory[0]);
                }
            } else {
                setSelectedCategoryId("");
            }

            // Set recommender model if it exists
            setSelectedRecommenderId(categoryItem.predictive?.recommenderId || "");
        }
    }, [selectedItem]);

    // Memoize available categories to prevent unnecessary recalculations
    useEffect(() => {
        if (!userProjectDataModels?.project) return;

        const collectCategories = () => {
            // Use a Map to track unique categories by ID
            const uniqueCategoriesMap = new Map<string, TCategoryOption>();
            const currentSelectedCategoryIds = getCurrentSelectedCategoryData().map(cat => cat.id);

            // Process all data models
            userProjectDataModels.project.dataModels?.forEach((dataModel, dataModelIndex) => {
                dataModel?.data?.productCategories?.forEach((productCategory, categoryIndex) => {
                    // Skip the current selected category
                    if (dataModelIndex === indexInfo.dataModelIndex && categoryIndex === indexInfo.categoryIndex) {
                        return;
                    }

                    productCategory?.data?.categories?.data?.forEach(category => {
                        // Skip categories that are in the current selected category
                        if (!currentSelectedCategoryIds.includes(category.id) && !uniqueCategoriesMap.has(category.id)) {
                            uniqueCategoriesMap.set(category.id, {
                                id: category.id,
                                name: category.name,
                                type: productCategory?.data?.categories?.type || "",
                                submodelName: dataModel?.data?.name
                            });
                        }
                    });
                });
            });

            // Process root level categories if any
            userProjectDataModels.project.productCategories?.forEach((productCategory, categoryIndex) => {
                // Skip the current selected category if it's at root level
                if (indexInfo.categoryOutsideDataModelIndex === categoryIndex) {
                    return;
                }

                productCategory?.data?.categories?.data?.forEach(category => {
                    // Skip categories that are in the current selected category
                    if (!currentSelectedCategoryIds.includes(category.id) && !uniqueCategoriesMap.has(category.id)) {
                        uniqueCategoriesMap.set(category.id, {
                            id: category.id,
                            name: category.name,
                            type: productCategory?.data?.categories?.type || "",
                            submodelName: "Root"
                        });
                    }
                });
            });

            // Convert Map values to array
            return Array.from(uniqueCategoriesMap.values());
        };

        setAvailableCategories(collectCategories());
    }, [userProjectDataModels, indexInfo, selectedItem]);

    // Fetch recommender models when base category changes or on initial load
    useEffect(() => {
        if (selectedCategoryId) {
            fetchRecommenderModels(selectedCategoryId);
        }
    }, [selectedCategoryId]);

    // Get the current selected category's data array
    const getCurrentSelectedCategoryData = () => {
        if (selectedItem?.type === "Category") {
            const categoryItem = selectedItem as TProductCategory;
            return categoryItem?.data?.categories?.data || [];
        }
        return [];
    };

    // Check if the selected category has categories data
    const hasCategories = (): boolean => {
        return getCurrentSelectedCategoryData().length > 0;
    };

    const categoriesExist = hasCategories();

    // Fetch recommender models
    const fetchRecommenderModels = async (categoryId: string) => {
        if (!categoryId || !channelKey) return;

        setIsLoadingModels(true);
        try {
            const response = await getRecommenderModelsForGuide(
                channelKey,
                [categoryId],
                getCurrentSelectedCategoryData().map(cat => cat.id)
            );

            if (response?.data && Array.isArray(response.data) && response.done) {
                setRecommenderModels(response.data);

                // If saved recommender ID does not exist in the new list, clear it
                if (selectedRecommenderId && !response.data.some((model: TRecommenderModel) => model.model_id === selectedRecommenderId)) {
                    setSelectedRecommenderId("");
                    updateProductCategory({ recommenderId: "" });
                }
            } else {
                setRecommenderModels([]);
            }
        } catch (error) {
            console.error("Error fetching recommender models:", error);
            setRecommenderModels([]);
        } finally {
            setIsLoadingModels(false);
        }
    };

    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setSelectedRecommenderId("");
        updateProductCategory({
            baseCategory: categoryId ? [categoryId] : undefined,
            recommenderId: undefined
        });
    };

    const handleRecommenderChange = (recommenderId: string) => {
        setSelectedRecommenderId(recommenderId);
        updateProductCategory({ recommenderId: recommenderId || undefined });
    };

    const handlePredictiveToggle = () => {
        const newState = !isPredictive;
        setIsPredictive(newState);

        // If turning off predictive, clear related fields
        if (!newState) {
            setSelectedCategoryId("");
            setSelectedRecommenderId("");
            updateProductCategory({
                isPredictive: false,
                baseCategory: undefined,
                recommenderId: undefined
            });
        } else {
            updateProductCategory({ isPredictive: true });
        }
    };

    // Update the product category with predictive settings
    const updateProductCategory = (updates: Partial<TPredictiveData>) => {
        if (!selectedItem || selectedItem.type !== "Category") return;

        setUserProjectDataModels(prevState => {
            // Create a deep copy to avoid mutation
            const newState = JSON.parse(JSON.stringify(prevState));
            let targetCategory: TProductCategory | undefined;

            // Find and update in data models
            if (typeof indexInfo.dataModelIndex === 'number' && typeof indexInfo.categoryIndex === 'number') {
                const dataModelIndex = indexInfo.dataModelIndex;
                const categoryIndex = indexInfo.categoryIndex;

                if (newState.project.dataModels[dataModelIndex]?.data?.productCategories?.[categoryIndex]) {
                    targetCategory = newState.project.dataModels[dataModelIndex].data.productCategories[categoryIndex];
                }
            }
            // Find and update at root level if needed
            else if (typeof indexInfo.categoryOutsideDataModelIndex === 'number') {
                const categoryIndex = indexInfo.categoryOutsideDataModelIndex;

                if (newState.project.productCategories?.[categoryIndex]) {
                    targetCategory = newState.project.productCategories[categoryIndex];
                }
            }

            // Apply updates if target category was found
            if (targetCategory) {
                if (!targetCategory.predictive) {
                    targetCategory.predictive = { isPredictive: false };
                }

                // Apply updates with proper type checking
                if ('isPredictive' in updates && updates.isPredictive !== undefined) {
                    targetCategory.predictive.isPredictive = updates.isPredictive;
                }
                if ('baseCategory' in updates) {
                    targetCategory.predictive.baseCategory = updates.baseCategory;
                }
                if ('recommenderId' in updates) {
                    targetCategory.predictive.recommenderId = updates.recommenderId;
                }
            }

            return newState;
        });
    };

    return (
        <>
            <div className="tw-rounded tw-bg-[#fff] tw-box-border tw-w-[98%] tw-h-auto tw-border-[1px] tw-border-solid tw-border-[#e5e5ea]">
                <div className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-pr-5">
                    <div className="tw-justify-start tw-text-black tw-text-xs tw-font-bold">Predictive</div>
                    <TooltipOverlay
                        message='Please choose a category to enable predictive'
                        disabled={categoriesExist}
                        placement='top'
                    >
                        <button
                            className={`tw-w-12 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-transition-colors ${isPredictive ? 'tw-bg-green-600' : 'tw-bg-gray-300'}`}
                            onClick={handlePredictiveToggle}
                            disabled={!categoriesExist}
                        >
                            <span
                                className={`tw-w-5 tw-h-5 tw-bg-white tw-rounded-full tw-shadow tw-transform tw-transition-transform ${isPredictive ? 'tw-translate-x-6' : 'tw-translate-x-1'}`}
                            />
                        </button>
                    </TooltipOverlay>
                </div>
            </div>
            {isPredictive && (
                <div className="tw-rounded-bl tw-rounded-br tw-bg-[#fff] tw-box-border tw-w-[98%] tw-h-auto tw-border-[1px] tw-border-solid tw-border-[#e5e5ea] tw-p-3">
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div>
                            <div className="tw-justify-start tw-text-slate-500 tw-text-sm tw-pb-1">Base Category *</div>
                            <select
                                className="tw-px-2.5 tw-h-8 tw-w-60 tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-zinc-200 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-text-slate-500 tw-text-sm tw-border tw-border-[#c9c9cf]"
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                value={selectedCategoryId}
                            >
                                <option value="">Select</option>
                                {availableCategories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="tw-justify-start tw-text-slate-500 tw-text-sm tw-pb-1">Recommender model</div>
                            <select
                                className="tw-px-2 tw-h-8 tw-w-60 tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-zinc-200 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-text-slate-500 tw-text-sm tw-border tw-border-[#c9c9cf]"
                                onChange={(e) => handleRecommenderChange(e.target.value)}
                                value={selectedRecommenderId}
                                disabled={isLoadingModels || !selectedCategoryId}
                            >
                                <option value="">
                                    {isLoadingModels ? "Loading..." : "Select"}
                                </option>
                                {recommenderModels.map((model) => (
                                    <option key={model.model_id} value={model.model_id}>
                                        {model.model_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PredictiveComponent;